import { useState } from 'react';
import { createPortal } from 'react-dom';
import { getPlayerAvatar } from '../../utils/player';
import ModalCloseButton from '../Modals/ModalCloseButton';

// Get first name from full name
const getFirstName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  return parts[0] || fullName;
};

// Helper to group games by set
const groupGamesIntoSets = (gamesList, setScoresList) => {
  if (!gamesList || gamesList.length === 0) return [];

  // Check if games already have setNumber attached
  const hasSetNumbers = gamesList.some((g) => g && g.setNumber !== undefined);
  if (hasSetNumbers) {
    const setsMap = {};
    gamesList.forEach((game) => {
      const sNum = game.setNumber || 1;
      if (!setsMap[sNum]) setsMap[sNum] = [];
      setsMap[sNum].push(game);
    });
    return Object.keys(setsMap)
      .sort((a, b) => Number(a) - Number(b))
      .map((sNum) => ({
        setNumber: Number(sNum),
        games: setsMap[sNum],
      }));
  }

  if (setScoresList && setScoresList.length > 0) {
    const result = [];
    let currentIdx = 0;

    setScoresList.forEach((pair, setIdx) => {
      const t1 = Array.isArray(pair) ? (pair[0] ?? 0) : (pair?.team1 ?? pair?.[0] ?? 0);
      const t2 = Array.isArray(pair) ? (pair[1] ?? 0) : (pair?.team2 ?? pair?.[1] ?? 0);
      const totalGamesInSet = (Number(t1) || 0) + (Number(t2) || 0);

      if (totalGamesInSet > 0) {
        const slice = gamesList.slice(currentIdx, currentIdx + totalGamesInSet);
        currentIdx += totalGamesInSet;
        if (slice.length > 0) {
          result.push({
            setNumber: setIdx + 1,
            setScore: [t1, t2],
            games: slice,
          });
        }
      }
    });

    if (currentIdx < gamesList.length) {
      const remaining = gamesList.slice(currentIdx);
      if (result.length > 0) {
        result[result.length - 1].games.push(...remaining);
      } else {
        result.push({ setNumber: 1, setScore: [0, 0], games: remaining });
      }
    }

    if (result.length > 0) return result;
  }

  return [{ setNumber: 1, setScore: [0, 0], games: gamesList }];
};

// Convert numeric or string score to tennis/padel display format ('0', '15', '30', '40')
const parseScorePoint = (val) => {
  if (val === undefined || val === null) return 0;
  if (typeof val === 'number') return val;
  const s = String(val).trim().toUpperCase();
  if (s === '15') return 1;
  if (s === '30') return 2;
  if (s === '40' || s === 'A' || s === 'G') return 3;
  const num = parseInt(s, 10);
  return isNaN(num) ? 0 : num;
};

const formatPadelGameScores = (scores) => {
  if (!scores) {
    return { winningTeamScore: '40', losingTeamScore: '0' };
  }

  let wRaw = 0;
  let lRaw = 0;

  if (scores.winningTeam !== undefined && scores.losingTeam !== undefined) {
    wRaw = parseScorePoint(scores.winningTeam);
    lRaw = parseScorePoint(scores.losingTeam);
  } else if (scores.team1 !== undefined && scores.team2 !== undefined) {
    wRaw = parseScorePoint(scores.team1);
    lRaw = parseScorePoint(scores.team2);
  }

  // Handle legacy inverted scores (where winner had 0 and loser had 4/40 in DB)
  if (wRaw < lRaw && wRaw === 0 && lRaw > 0) {
    const temp = wRaw;
    wRaw = lRaw;
    lRaw = temp;
  }

  // Map numeric points to tennis strings ('0', '15', '30', '40') or tiebreak digits
  const toPointString = (pts, isWinner) => {
    if (pts === 0 || pts === '0') return '0';
    if (pts === 1 || pts === 15 || pts === '15') return '15';
    if (pts === 2 || pts === 30 || pts === '30') return '30';
    if (pts === 3 || pts === 40 || pts === '40' || pts === 4) return '40';
    if (pts > 4 && pts !== 15 && pts !== 30 && pts !== 40) return String(pts);
    return isWinner ? '40' : '0';
  };

  let winStr = toPointString(wRaw, true);
  let lossStr = toPointString(lRaw, false);

  if (winStr === '0' && lossStr === '0') {
    winStr = '40';
  }

  return {
    winningTeamScore: winStr,
    losingTeamScore: lossStr,
  };
};

// Format duration in seconds to "Xm Ys" format
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

// Calculate game duration (Game 1 = session start to Game 1 end; Game N = Game N-1 end to Game N end)
const calculateGameDuration = (game, previousGame, session) => {
  if (!game || !game.timestamp) return null;
  
  const gameEndMs = typeof game.timestamp === 'number' ? game.timestamp : new Date(game.timestamp).getTime();
  if (isNaN(gameEndMs)) return null;

  let gameStartMs = null;

  if (game.gameStartTime) {
    gameStartMs = typeof game.gameStartTime === 'number' 
      ? game.gameStartTime 
      : new Date(game.gameStartTime).getTime();
  } else if (previousGame && previousGame.timestamp) {
    // Game N (N > 1): From previous game end to current game end
    gameStartMs = typeof previousGame.timestamp === 'number' 
      ? previousGame.timestamp 
      : new Date(previousGame.timestamp).getTime();
  } else {
    // Game 1: From session start to Game 1 end
    const rawStart = session?.startTimestamp || session?.createdAt;
    if (rawStart) {
      const startMs = typeof rawStart === 'number' ? rawStart : new Date(rawStart).getTime();
      if (!isNaN(startMs) && startMs < gameEndMs) {
        gameStartMs = startMs;
      }
    }
  }

  if (!gameStartMs || isNaN(gameStartMs) || gameStartMs >= gameEndMs) {
    return 0;
  }

  const durationMs = gameEndMs - gameStartMs;
  const durationSec = Math.floor(durationMs / 1000);
  return durationSec >= 0 ? durationSec : 0;
};

const GameDetailsModal = ({ session, allPlayers, onClose }) => {
  const [activeSetTab, setActiveSetTab] = useState(0);

  if (!session) {
    return null;
  }

  const rawHistory = (session.sessionHistory && session.sessionHistory.length > 0)
    ? session.sessionHistory
    : (session.classicGameHistory || []);

  if (rawHistory.length === 0 && !session.matchSummary && !session.setScores) {
    return null;
  }

  const firstEntry = rawHistory[0];
  const isClassic = session.sessionMode === 'classic' || session.classicMatch || firstEntry?.classicMatch;
  const matchSummary = session.matchSummary || (isClassic ? firstEntry : null);

  // Filter individual game records (items that represent a completed game with winning/losing teams)
  let individualGames = rawHistory.filter(
    (g) => g && (g.winningTeam || g.winningTeamScore) && (g.losingTeam || g.losingTeamScore || g.scores)
  );

  // Extract set scores for classic match header
  let team1 = [];
  let team2 = [];
  let setScores = [];
  let numSetColumns = 1;

  if (isClassic) {
    const toPlayerArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === 'object' && val !== null) {
        if (val.name || val.id || val.uid) return [val];
        const values = Object.values(val);
        if (values.length > 0) return values;
      }
      if (typeof val === 'string') return [val];
      return [];
    };

    const formatPlayerName = (p) => {
      if (!p) return '';
      if (typeof p === 'object') return p.name || p.id || p.uid || String(p);
      return String(p);
    };

    const team1Candidates = [
      toPlayerArray(matchSummary?.team1),
      toPlayerArray(matchSummary?.winningTeam),
      toPlayerArray(session?.team1),
      toPlayerArray(session?.winningTeam),
      toPlayerArray(session?.sessionPlayers).slice(0, 2),
      toPlayerArray(firstEntry?.winningTeam),
      toPlayerArray(firstEntry?.team1),
    ];

    const rawTeam1 = team1Candidates.find((arr) => arr.length > 0) || [];

    const team2Candidates = [
      toPlayerArray(matchSummary?.team2),
      toPlayerArray(matchSummary?.losingTeam),
      toPlayerArray(session?.team2),
      toPlayerArray(session?.losingTeam),
      toPlayerArray(session?.sessionPlayers).slice(2, 4),
      toPlayerArray(firstEntry?.losingTeam),
      toPlayerArray(firstEntry?.team2),
    ];

    const rawTeam2 = team2Candidates.find((arr) => arr.length > 0) || [];

    team1 = rawTeam1.map(formatPlayerName).filter(Boolean);
    team2 = rawTeam2.map(formatPlayerName).filter(Boolean);

    const rawSetScoresCandidates = [
      toPlayerArray(session?.setScores),
      toPlayerArray(matchSummary?.setScores),
      toPlayerArray(firstEntry?.setScores),
    ];

    const rawSetScores = rawSetScoresCandidates.find((arr) => arr.length > 0) || [];
    setScores = rawSetScores.map((p) => {
      if (Array.isArray(p)) return [p[0] ?? 0, p[1] ?? 0];
      if (p && typeof p === 'object') return [p.team1 ?? p[0] ?? 0, p.team2 ?? p[1] ?? 0];
      return [0, 0];
    });

    if (setScores.length === 0) {
      setScores.push([0, 0]);
    }

    // If no individual game logs exist for this classic match, generate synthetic game breakdown per set
    if (individualGames.length === 0 && setScores.length > 0) {
      setScores.forEach((pair, sIdx) => {
        const t1 = Array.isArray(pair) ? (pair[0] || 0) : (pair?.team1 || 0);
        const t2 = Array.isArray(pair) ? (pair[1] || 0) : (pair?.team2 || 0);
        const total = t1 + t2;
        if (total === 0) return;

        let g1Count = 0;
        let g2Count = 0;

        for (let i = 0; i < total; i++) {
          let isTeam1Winner = false;
          if (g1Count < t1 && (g2Count >= t2 || i % 2 === 0)) {
            isTeam1Winner = true;
            g1Count++;
          } else if (g2Count < t2) {
            isTeam1Winner = false;
            g2Count++;
          } else {
            isTeam1Winner = true;
            g1Count++;
          }

          individualGames.push({
            winningTeam: isTeam1Winner ? team1 : team2,
            losingTeam: isTeam1Winner ? team2 : team1,
            scores: { winningTeam: '40', losingTeam: '15' },
            classicMatch: true,
            setNumber: sIdx + 1,
            gameNumberInSet: i + 1,
          });
        }
      });
    }

    const bestOfSets = session?.bestOfSets || matchSummary?.bestOfSets || (setScores.length > 1 ? 3 : 1);
    numSetColumns = bestOfSets === 1 ? Math.max(setScores.length, 1) : Math.max(setScores.length, 3);
  }

  // Calculate durations for all individual games
  const games = individualGames.map((game, index) => {
    const previousGame = index > 0 ? individualGames[index - 1] : null;
    const duration = calculateGameDuration(game, previousGame, session);
    
    return {
      ...game,
      duration
    };
  });

  // Group games into sets
  const setsList = groupGamesIntoSets(games, setScores);
  const displayedGames = setsList.length > 1 ? (setsList[activeSetTab]?.games || []) : games;

  return createPortal(
    <div className="modal-overlay">
      <div className="modal modal-wide" style={{ 
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden'
      }}>
        <ModalCloseButton onClick={onClose} />
        
        <h3 className="modal-title" style={{ margin: 0, padding: '24px 56px 16px', flexShrink: 0 }}>
          {isClassic ? 'Match details' : 'Game details'}
        </h3>

        <div className="modal-content" style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          minHeight: 0,
          padding: '0 24px 24px',
          marginBottom: 0,
          textAlign: 'left'
        }}>
          {/* Classic Match Summary Set Score Header */}
          {isClassic && matchSummary && (
            <div className="mb-6 p-4 rounded-xl bg-slate-900/60 border border-white/10">
              {/* TEAM 1 & SET BLOCKS ROW */}
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                  {(team1 || []).map((name) => (
                    <div key={name} className="flex items-center gap-3">
                      <img
                        src={getPlayerAvatar(allPlayers, name)}
                        alt={name}
                        className="avatar avatar-sm avatar-border"
                      />
                      <span className="font-extrabold text-slate-100 text-sm truncate">{name}</span>
                    </div>
                  ))}
                </div>

                {/* TEAM 1 SET SCORE BLOCKS */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {Array.from({ length: numSetColumns }, (_, i) => {
                    const pair = setScores[i];
                    if (!pair) {
                      return (
                        <div
                          key={i}
                          className="w-9 h-11 rounded-[8px] bg-[#434e75] flex items-center justify-center font-bold text-lg text-black shadow-md"
                        >
                          -
                        </div>
                      );
                    }
                    const isSetWon = (v1, v2) => (v1 >= 6 && v1 - v2 >= 2) || v1 === 7;
                    const t1Val = pair[0];
                    const t2Val = pair[1];
                    const t1Won = isSetWon(t1Val, t2Val);
                    return (
                      <div
                        key={i}
                        className={`w-9 h-11 flex items-center justify-center font-bold text-lg text-black shadow-md transition-all ${
                          t1Won
                            ? 'bg-[#f5b83d] rounded-[8px]'
                            : 'bg-[#a6c5ff] rounded-[8px]'
                        }`}
                      >
                        {t1Val}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                className="my-3"
                style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.12)' }}
              />

              {/* TEAM 2 & SET BLOCKS ROW */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                  {(team2 || []).map((name) => (
                    <div key={name} className="flex items-center gap-3">
                      <img
                        src={getPlayerAvatar(allPlayers, name)}
                        alt={name}
                        className="avatar avatar-sm avatar-border"
                      />
                      <span className="font-extrabold text-slate-100 text-sm truncate">{name}</span>
                    </div>
                  ))}
                </div>

                {/* TEAM 2 SET SCORE BLOCKS */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {Array.from({ length: numSetColumns }, (_, i) => {
                    const pair = setScores[i];
                    if (!pair) {
                      return (
                        <div
                          key={i}
                          className="w-9 h-11 rounded-[8px] bg-[#434e75] flex items-center justify-center font-bold text-lg text-black shadow-md"
                        >
                          -
                        </div>
                      );
                    }
                    const isSetWon = (v1, v2) => (v1 >= 6 && v1 - v2 >= 2) || v1 === 7;
                    const t1Val = pair[0];
                    const t2Val = pair[1];
                    const t2Won = isSetWon(t2Val, t1Val);
                    return (
                      <div
                        key={i}
                        className={`w-9 h-11 flex items-center justify-center font-bold text-lg text-black shadow-md transition-all ${
                          t2Won
                            ? 'bg-[#f5b83d] rounded-[8px]'
                            : 'bg-[#a6c5ff] rounded-[8px]'
                        }`}
                      >
                        {t2Val}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Set Tabs for Multi-Set Matches */}
          {setsList.length > 1 && (
            <div className="tabs-container mb-6" style={{ '--tab-count': setsList.length }}>
              <div className="tabs-group">
                {setsList.map((setObj, idx) => {
                  const isSelected = activeSetTab === idx;
                  return (
                    <div
                      key={idx}
                      className={`tab ${isSelected ? 'tab-selected' : 'tab-unselected'}`}
                      onClick={() => setActiveSetTab(idx)}
                    >
                      <div className="tab-contents">
                        <span className="tab-label">SET {setObj.setNumber}</span>
                      </div>
                    </div>
                  );
                })}
                <div
                  className="tab-indicator"
                  style={{
                    left: `${(activeSetTab * 100) / setsList.length}%`,
                    width: `${100 / setsList.length}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Section Divider if Classic Match has Game History and single set */}
          {isClassic && games.length > 0 && setsList.length <= 1 && (
            <div className="text-center font-bold text-xs uppercase tracking-wider text-slate-400 mb-4">
              Game-by-Game Breakdown
            </div>
          )}

          {isClassic && games.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm italic">
              No game-by-game breakdown was recorded for this session.
            </div>
          )}

          {/* Individual Games List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            {displayedGames.map((game, index) => {
              const winningTeam = game.winningTeam || [];
              const losingTeam = game.losingTeam || [];
              const scores = game.scores || {};
              
              const { winningTeamScore, losingTeamScore } = formatPadelGameScores(scores);
              
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: 'var(--surface-container-light)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-md)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-sm)'
                  }}
                >
                  {/* Teams and Scores Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--spacing-md)'
                  }}>
                    {/* Winning Team (Left) */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-sm)',
                      flex: 1,
                      minWidth: 0,
                      justifyContent: 'flex-end',
                    }}>
                      {/* Overlapping Avatars */}
                      <div style={{
                        display: 'flex',
                        position: 'relative',
                        marginRight: 'var(--spacing-xs)'
                      }}>
                        {winningTeam.map((player, idx) => (
                          <img
                            key={player}
                            src={getPlayerAvatar(allPlayers, player)}
                            alt={player}
                            className="avatar avatar-sm avatar-border"
                            style={{
                              marginLeft: idx > 0 ? '-8px' : 0,
                              zIndex: winningTeam.length - idx,
                              position: 'relative'
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Score Badge */}
                      <div className="w-11 h-11 min-w-[44px] bg-[#f5b83d] rounded-[8px] flex items-center justify-center font-bold text-lg text-black shadow-md flex-shrink-0">
                        {winningTeamScore}
                      </div>
                    </div>

                    {/* Losing Team (Right) */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-sm)',
                      flex: 1,
                      justifyContent: 'flex-start',
                      minWidth: 0
                    }}>
                      {/* Score Badge */}
                      <div className="w-11 h-11 min-w-[44px] bg-[#434e75] rounded-[8px] flex items-center justify-center font-bold text-lg text-white shadow-md flex-shrink-0">
                        {losingTeamScore}
                      </div>
                      
                      {/* Overlapping Avatars */}
                      <div style={{
                        display: 'flex',
                        position: 'relative',
                        marginLeft: 'var(--spacing-xs)'
                      }}>
                        {losingTeam.map((player, idx) => (
                          <img
                            key={player}
                            src={getPlayerAvatar(allPlayers, player)}
                            alt={player}
                            className="avatar avatar-sm avatar-border"
                            style={{
                              marginLeft: idx > 0 ? '-8px' : 0,
                              zIndex: losingTeam.length - idx,
                              position: 'relative'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Result Text */}
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--on-surface-variant)',
                    textAlign: 'center',
                    marginTop: 'var(--spacing-xs)'
                  }}>
                    {winningTeam.map(p => getFirstName(p)).join(' & ')} beat {losingTeam.map(p => getFirstName(p)).join(' & ')}
                  </div>

                  {/* Game Time */}
                  {game.duration !== null && (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--secondary)',
                      textAlign: 'center'
                    }}>
                      Game time: {formatDuration(game.duration)}
                    </div>
                  )}

                  {/* Server */}
                  {(game.serving || game.server) && (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--secondary)',
                      textAlign: 'center'
                    }}>
                      Server: {game.serving || game.server}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GameDetailsModal;

